/* =====================================================================
 * pptxParser.ts — OOXML(PPTX) → 내부 모델 파서
 * JSZip + DOMParser 기반으로 presentation.xml, slideN.xml, theme1.xml,
 * slideMasters, slideLayouts, media/*, _rels 를 파싱
 * ===================================================================== */

import JSZip from 'jszip';

/* ------------------------------------------------------------------ */
/* Types — 내부 프레젠테이션 모델                                         */
/* ------------------------------------------------------------------ */

export interface PptxPresentation {
  slides: PptxSlide[];
  slideSize: { width: number; height: number };
  theme: PptxTheme;
  slideMasters: PptxSlideMaster[];
  slideLayouts: PptxSlideLayout[];
  media: Map<string, string>;
  rawParts: Map<string, string>;
  zip: JSZip;
  /** 문서 전체에서 실제 텍스트 런에 사용된 폰트 이름 목록 */
  usedFonts: string[];
}

export interface PptxSlide {
  id: string;
  index: number;
  partPath: string;
  shapes: PptxShape[];
  background?: PptxBackground;
  layoutRef?: string;
  colorMap?: PptxColorMap;
  dirty?: boolean;
}

export interface PptxImageCrop {
  left: number;    // 0~100000 (100000 = 100%)
  top: number;
  right: number;
  bottom: number;
}

export interface PptxShape {
  id: string;
  name: string;
  type: 'textbox' | 'image' | 'shape' | 'table' | 'group' | 'connector';
  position: { x: number; y: number };
  size: { width: number; height: number };
  hasExplicitTransform?: boolean;
  rotation?: number;
  flipH?: boolean;
  flipV?: boolean;
  textBody?: PptxTextBody;
  geometry?: PptxGeometry;
  fill?: PptxFill;
  outline?: PptxOutline;
  imageRId?: string;
  imageCrop?: PptxImageCrop;
  placeholder?: { type: string; idx?: string };
  children?: PptxShape[];
  tableGrid?: PptxTableGrid;
  groupChildOffset?: { x: number; y: number };
  groupChildExtent?: { width: number; height: number };
}

export interface PptxParagraphDefaults {
  fontSize?: number;
  fontFamily?: string;
  bold?: boolean;
  italic?: boolean;
  color?: string;
}

export interface PptxGeometry {
  preset?: string;
  paths?: string[];
}

export interface PptxFill {
  type: 'solid' | 'gradient' | 'pattern' | 'none';
  color?: string;
  gradientStops?: { position: number; color: string }[];
}

export interface PptxOutline {
  width?: number;
  color?: string;
  dashStyle?: string;
}

export interface PptxBackground {
  fill?: PptxFill;
}

export interface PptxTextBody {
  paragraphs: PptxParagraph[];
  bodyProps: PptxBodyProps;
}

export interface PptxBodyProps {
  anchor?: string;
  anchorCtr?: boolean;
  wrap?: string;
  horzOverflow?: string;
  vertOverflow?: string;
  lIns?: number;
  tIns?: number;
  rIns?: number;
  bIns?: number;
  autofit?: 'none' | 'normal' | 'shrink';
}

export interface PptxParagraph {
  id: string;
  runs: PptxRun[];
  alignment?: string;
  bulletType?: 'none' | 'char' | 'autoNum';
  bulletChar?: string;
  bulletAutoNumType?: string;
  bulletFontFamily?: string;
  level?: number;
  spacing?: { before?: number; after?: number; lineSpacing?: number };
  indent?: {
    left?: number;      // marL: 텍스트 왼쪽 마진 (px)
    hanging?: number;   // 양수: 첫 줄 왼쪽으로 내어쓰기(hanging) 크기 (px) → textIndent에 음수로 적용
    firstLine?: number; // 양수: 첫 줄 들여쓰기 크기 (px) → textIndent에 양수로 적용
  };
  defaultRunProps?: PptxParagraphDefaults;
}

export interface PptxRun {
  id: string;
  type: 'text' | 'break' | 'field';
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  fontSize?: number;
  fontFamily?: string;
  color?: string;
  highlight?: string;
}

export interface PptxTableGrid {
  rows: PptxTableRow[];
  gridCols: number[];
}

export interface PptxTableRow {
  height: number;
  cells: PptxTableCell[];
}

export interface PptxTableCell {
  textBody?: PptxTextBody;
  colSpan?: number;
  rowSpan?: number;
  hMerge?: boolean;
  vMerge?: boolean;
  fill?: PptxFill;
  borders?: {
    top?: PptxBorder;
    bottom?: PptxBorder;
    left?: PptxBorder;
    right?: PptxBorder;
  };
  padding?: { left: number; right: number; top: number; bottom: number };
  anchor?: string;
}

export interface PptxBorder {
  width?: number;
  color?: string;
  style?: string;
}

export interface PptxTheme {
  name: string;
  colors: PptxThemeColors;
  fonts: { major: string; minor: string };
  colorMap: PptxColorMap;
  lineStyles: PptxOutline[];
}

export interface PptxThemeColors {
  dk1: string;
  lt1: string;
  dk2: string;
  lt2: string;
  accent1: string;
  accent2: string;
  accent3: string;
  accent4: string;
  accent5: string;
  accent6: string;
  hlink: string;
  folHlink: string;
}

export interface PptxColorMap {
  bg1: keyof PptxThemeColors;
  tx1: keyof PptxThemeColors;
  bg2: keyof PptxThemeColors;
  tx2: keyof PptxThemeColors;
  accent1: keyof PptxThemeColors;
  accent2: keyof PptxThemeColors;
  accent3: keyof PptxThemeColors;
  accent4: keyof PptxThemeColors;
  accent5: keyof PptxThemeColors;
  accent6: keyof PptxThemeColors;
  hlink: keyof PptxThemeColors;
  folHlink: keyof PptxThemeColors;
}

export interface PptxSlideMaster {
  partPath: string;
  background?: PptxBackground;
  shapes: PptxShape[];
  colorMap?: PptxColorMap;
  themeRef?: string;
}

export interface PptxSlideLayout {
  partPath: string;
  background?: PptxBackground;
  shapes: PptxShape[];
  colorMap?: PptxColorMap;
  masterRef?: string;
}

/* ------------------------------------------------------------------ */
/* XML 네임스페이스                                                      */
/* ------------------------------------------------------------------ */

const P_NS = 'http://schemas.openxmlformats.org/presentationml/2006/main';
const A_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';
const R_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const REL_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';

/* ------------------------------------------------------------------ */
/* 유틸리티                                                              */
/* ------------------------------------------------------------------ */

let _idCounter = 0;
function genId(): string {
  return `pptx_${Date.now()}_${++_idCounter}`;
}

/** EMU → px (1 EMU = 1/914400 inch, 96 DPI) */
function emuToPx(emu: number): number {
  return (emu / 914400) * 96;
}

/** px → EMU */
export function pxToEmu(px: number): number {
  return Math.round((px / 96) * 914400);
}

/** hundredths of a point → px (1 pt = 100 hundredths, 1 pt ≈ 1.333px) */
export function centiPtToPx(cp: number): number {
  return cp / 100;
}

function getEl(parent: Element, ns: string, localName: string): Element | null {
  return parent.getElementsByTagNameNS(ns, localName)[0] || null;
}

function getAllEl(parent: Element, ns: string, localName: string): Element[] {
  return Array.from(parent.getElementsByTagNameNS(ns, localName));
}

function directChildrenNS(parent: Element, ns: string, localName: string): Element[] {
  const result: Element[] = [];
  for (let i = 0; i < parent.childNodes.length; i++) {
    const child = parent.childNodes[i];
    if (
      child.nodeType === 1 &&
      (child as Element).localName === localName &&
      (child as Element).namespaceURI === ns
    ) {
      result.push(child as Element);
    }
  }
  return result;
}

function attr(el: Element, name: string): string | null {
  return el.getAttribute(name);
}

async function readPart(zip: JSZip, path: string): Promise<string | null> {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const file = zip.file(cleanPath);
  if (!file) return null;
  return await file.async('string');
}

/* ------------------------------------------------------------------ */
/* 테마 색상 해석                                                        */
/* ------------------------------------------------------------------ */

const DEFAULT_COLOR_MAP: PptxColorMap = {
  bg1: 'lt1',
  tx1: 'dk1',
  bg2: 'lt2',
  tx2: 'dk2',
  accent1: 'accent1',
  accent2: 'accent2',
  accent3: 'accent3',
  accent4: 'accent4',
  accent5: 'accent5',
  accent6: 'accent6',
  hlink: 'hlink',
  folHlink: 'folHlink',
};

const DEFAULT_THEME: PptxTheme = {
  name: 'Default',
  colors: {
    dk1: '#000000', lt1: '#FFFFFF',
    dk2: '#44546A', lt2: '#E7E6E6',
    accent1: '#4472C4', accent2: '#ED7D31',
    accent3: '#A5A5A5', accent4: '#FFC000',
    accent5: '#5B9BD5', accent6: '#70AD47',
    hlink: '#0563C1', folHlink: '#954F72',
  },
  fonts: { major: 'Calibri', minor: 'Calibri' },
  colorMap: { ...DEFAULT_COLOR_MAP },
  lineStyles: [
    { width: 1, color: '#000000' },
    { width: 2, color: '#000000' },
    { width: 3, color: '#000000' },
  ],
};

function parseTheme(xml: string): PptxTheme {
  const dom = new DOMParser().parseFromString(xml, 'application/xml');
  const theme: PptxTheme = {
    ...DEFAULT_THEME,
    colors: { ...DEFAULT_THEME.colors },
    fonts: { ...DEFAULT_THEME.fonts },
    colorMap: { ...DEFAULT_THEME.colorMap },
    lineStyles: DEFAULT_THEME.lineStyles.map((line) => ({ ...line })),
  };

  const themeEl = dom.documentElement;
  theme.name = attr(themeEl, 'name') || 'Default';

  const clrScheme = getEl(dom.documentElement, A_NS, 'clrScheme');
  if (clrScheme) {
    const colorNames: (keyof PptxThemeColors)[] = [
      'dk1', 'lt1', 'dk2', 'lt2',
      'accent1', 'accent2', 'accent3', 'accent4', 'accent5', 'accent6',
      'hlink', 'folHlink',
    ];
    for (const name of colorNames) {
      const el = getEl(clrScheme, A_NS, name);
      if (!el) continue;
      const color = extractColorFromElement(el);
      if (color) theme.colors[name] = color;
    }
  }

  const majorFont = getEl(dom.documentElement, A_NS, 'majorFont');
  if (majorFont) {
    const latin = getEl(majorFont, A_NS, 'latin');
    if (latin) {
      const typeface = attr(latin, 'typeface');
      if (typeface) theme.fonts.major = typeface;
    }
  }

  const minorFont = getEl(dom.documentElement, A_NS, 'minorFont');
  if (minorFont) {
    const latin = getEl(minorFont, A_NS, 'latin');
    if (latin) {
      const typeface = attr(latin, 'typeface');
      if (typeface) theme.fonts.minor = typeface;
    }
  }

  const lnStyleLst = getEl(dom.documentElement, A_NS, 'lnStyleLst');
  if (lnStyleLst) {
    const parsedLineStyles = directChildrenNS(lnStyleLst, A_NS, 'ln')
      .map((lnEl) => parseOutline(lnEl, theme))
      .filter((line): line is PptxOutline => !!line);
    if (parsedLineStyles.length > 0) {
      theme.lineStyles = parsedLineStyles;
    }
  }

  return theme;
}

function resolveThemeTypeface(typeface: string | null, theme: PptxTheme): string | undefined {
  if (!typeface) return undefined;
  if (typeface.startsWith('+mj')) return theme.fonts.major;
  if (typeface.startsWith('+mn')) return theme.fonts.minor;
  return typeface;
}

function isThemeColorKey(value: string | null): value is keyof PptxThemeColors {
  return !!value && value in DEFAULT_THEME.colors;
}

function parseColorMapEl(el: Element | null): Partial<PptxColorMap> | undefined {
  if (!el) return undefined;
  const keys: (keyof PptxColorMap)[] = [
    'bg1', 'tx1', 'bg2', 'tx2',
    'accent1', 'accent2', 'accent3', 'accent4', 'accent5', 'accent6',
    'hlink', 'folHlink',
  ];
  const colorMap: Partial<PptxColorMap> = {};
  for (const key of keys) {
    const mapped = attr(el, key);
    if (isThemeColorKey(mapped)) {
      colorMap[key] = mapped;
    }
  }
  return Object.keys(colorMap).length > 0 ? colorMap : undefined;
}

function mergeColorMap(base: PptxColorMap, override?: Partial<PptxColorMap>): PptxColorMap {
  return {
    ...base,
    ...(override || {}),
  };
}

function withColorMap(theme: PptxTheme, colorMap?: PptxColorMap): PptxTheme {
  if (!colorMap) return theme;
  return { ...theme, colorMap };
}

function parseColorMapOverride(rootEl: Element | null): Partial<PptxColorMap> | 'master' | undefined {
  if (!rootEl) return undefined;
  const clrMapOvr = getEl(rootEl, P_NS, 'clrMapOvr') || getEl(rootEl, A_NS, 'clrMapOvr');
  if (!clrMapOvr) return undefined;

  const masterClrMapping = getEl(clrMapOvr, A_NS, 'masterClrMapping')
    || getEl(clrMapOvr, P_NS, 'masterClrMapping');
  if (masterClrMapping) return 'master';

  const overrideClrMapping = getEl(clrMapOvr, A_NS, 'overrideClrMapping')
    || getEl(clrMapOvr, P_NS, 'overrideClrMapping');
  return parseColorMapEl(overrideClrMapping || clrMapOvr);
}

function extractColorFromElement(el: Element): string | null {
  const srgb = getEl(el, A_NS, 'srgbClr');
  if (srgb) {
    const val = attr(srgb, 'val');
    if (val) return `#${val}`;
  }
  const sys = getEl(el, A_NS, 'sysClr');
  if (sys) {
    const lastClr = attr(sys, 'lastClr');
    if (lastClr) return `#${lastClr}`;
  }
  return null;
}

/** scheme color name → hex */
export function resolveSchemeColor(name: string, theme: PptxTheme): string {
  const colorMap = theme.colorMap;
  const key = (name in colorMap
    ? colorMap[name as keyof PptxColorMap]
    : (isThemeColorKey(name) ? name : undefined));
  return key ? theme.colors[key] : '#000000';
}

/* ------------------------------------------------------------------ */
/* 색상 파싱 (여러 형태 지원)                                              */
/* ------------------------------------------------------------------ */

/** prstClr name → hex */
const PRESET_COLORS: Record<string, string> = {
  black: '#000000', white: '#FFFFFF', red: '#FF0000', green: '#008000',
  blue: '#0000FF', yellow: '#FFFF00', cyan: '#00FFFF', magenta: '#FF00FF',
  darkBlue: '#00008B', darkCyan: '#008B8B', darkGray: '#A9A9A9', darkGreen: '#006400',
  darkMagenta: '#8B008B', darkRed: '#8B0000', darkYellow: '#808000',
  gray: '#808080', lightGray: '#D3D3D3', orange: '#FFA500', purple: '#800080',
  pink: '#FFC0CB', brown: '#A52A2A', lime: '#00FF00', silver: '#C0C0C0',
  gold: '#FFD700', ivory: '#FFFFF0', lavender: '#E6E6FA', maroon: '#800000',
  navy: '#000080', olive: '#808000', teal: '#008080', violet: '#EE82EE',
};

/**
 * schemeClr의 lumMod/lumOff/tint/shade 변환
 * Microsoft OOXML 명세 기준: 0~100000 스케일 (100000 = 100%)
 */
function applySchemeColorModifiers(hexColor: string, modEl: Element): string {
  let r = parseInt(hexColor.slice(1, 3), 16);
  let g = parseInt(hexColor.slice(3, 5), 16);
  let b = parseInt(hexColor.slice(5, 7), 16);

  const lumModEl = getEl(modEl, A_NS, 'lumMod');
  const lumOffEl = getEl(modEl, A_NS, 'lumOff');
  const tintEl = getEl(modEl, A_NS, 'tint');
  const shadeEl = getEl(modEl, A_NS, 'shade');

  // Convert RGB → HLS for lum operations
  const rNorm = r / 255, gNorm = g / 255, bNorm = b / 255;
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let l = (max + min) / 2;
  let s = 0, h = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rNorm: h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6; break;
      case gNorm: h = ((bNorm - rNorm) / d + 2) / 6; break;
      case bNorm: h = ((rNorm - gNorm) / d + 4) / 6; break;
    }
  }

  if (lumModEl) l = l * (parseInt(attr(lumModEl, 'val') || '100000', 10) / 100000);
  if (lumOffEl) l = l + (parseInt(attr(lumOffEl, 'val') || '0', 10) / 100000);
  l = Math.max(0, Math.min(1, l));

  // HLS → RGB
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  if (s === 0) {
    r = g = b = Math.round(l * 255);
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
    g = Math.round(hue2rgb(p, q, h) * 255);
    b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  }

  // tint: mix toward white
  if (tintEl) {
    const t = parseInt(attr(tintEl, 'val') || '100000', 10) / 100000;
    r = Math.round(r + (255 - r) * (1 - t));
    g = Math.round(g + (255 - g) * (1 - t));
    b = Math.round(b + (255 - b) * (1 - t));
  }
  // shade: mix toward black
  if (shadeEl) {
    const s2 = parseInt(attr(shadeEl, 'val') || '100000', 10) / 100000;
    r = Math.round(r * s2);
    g = Math.round(g * s2);
    b = Math.round(b * s2);
  }

  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
}

/**
 * alpha 요소를 읽어 rgba() 혹은 hex 반환
 * PPTX alpha: 0 = 완전 투명, 100000 = 완전 불투명
 */
function applyAlpha(hex: string, colorEl: Element): string {
  const alphaEl = getEl(colorEl, A_NS, 'alpha');
  if (!alphaEl) return hex;
  const val = parseInt(attr(alphaEl, 'val') || '100000', 10);
  if (val >= 100000) return hex;
  const opacity = val / 100000;
  if (opacity <= 0) return 'transparent';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${opacity.toFixed(3)})`;
}

function parseColor(el: Element, theme: PptxTheme): string | undefined {
  const srgb = getEl(el, A_NS, 'srgbClr');
  if (srgb) {
    const val = attr(srgb, 'val');
    if (!val) return undefined;
    const hex = `#${val}`.toUpperCase();
    return applyAlpha(hex, srgb);
  }
  const schemeClr = getEl(el, A_NS, 'schemeClr');
  if (schemeClr) {
    const val = attr(schemeClr, 'val');
    if (val) {
      const baseColor = resolveSchemeColor(val, theme);
      const hasModifier = schemeClr.getElementsByTagNameNS(A_NS, 'lumMod').length > 0
        || schemeClr.getElementsByTagNameNS(A_NS, 'lumOff').length > 0
        || schemeClr.getElementsByTagNameNS(A_NS, 'tint').length > 0
        || schemeClr.getElementsByTagNameNS(A_NS, 'shade').length > 0;
      const hex = hasModifier ? applySchemeColorModifiers(baseColor, schemeClr) : baseColor;
      return applyAlpha(hex, schemeClr);
    }
  }
  const sysClr = getEl(el, A_NS, 'sysClr');
  if (sysClr) {
    const lastClr = attr(sysClr, 'lastClr');
    if (!lastClr) return undefined;
    const hex = `#${lastClr}`.toUpperCase();
    return applyAlpha(hex, sysClr);
  }
  const prstClr = getEl(el, A_NS, 'prstClr');
  if (prstClr) {
    const val = attr(prstClr, 'val');
    if (val) {
      const hex = PRESET_COLORS[val] ?? '#000000';
      return applyAlpha(hex, prstClr);
    }
  }
  return undefined;
}

/* ------------------------------------------------------------------ */
/* Fill 파싱                                                            */
/* ------------------------------------------------------------------ */

function parseFill(parent: Element, theme: PptxTheme): PptxFill | undefined {
  const directSolidFill = directChildrenNS(parent, A_NS, 'solidFill')[0];
  if (directSolidFill) {
    return { type: 'solid', color: parseColor(directSolidFill, theme) };
  }

  const directGradFill = directChildrenNS(parent, A_NS, 'gradFill')[0];
  if (directGradFill) {
    const stops: PptxFill['gradientStops'] = [];
    const gsLst = getEl(directGradFill, A_NS, 'gsLst');
    if (gsLst) {
      const gsEls = directChildrenNS(gsLst, A_NS, 'gs');
      for (const gs of gsEls) {
        const pos = parseInt(attr(gs, 'pos') || '0', 10) / 1000;
        const color = parseColor(gs, theme);
        if (color) stops.push({ position: pos, color });
      }
    }
    return { type: 'gradient', gradientStops: stops };
  }

  const directNoFill = directChildrenNS(parent, A_NS, 'noFill')[0];
  if (directNoFill) return { type: 'none' };

  return undefined;
}

/* ------------------------------------------------------------------ */
/* Outline 파싱                                                         */
/* ------------------------------------------------------------------ */

function parseOutline(lnEl: Element, theme: PptxTheme): PptxOutline | undefined {
  const noFill = getEl(lnEl, A_NS, 'noFill');
  if (noFill) return undefined;

  const outline: PptxOutline = {};
  const w = attr(lnEl, 'w');
  if (w) outline.width = emuToPx(parseInt(w, 10));

  const solidFill = getEl(lnEl, A_NS, 'solidFill');
  if (solidFill) {
    const color = parseColor(solidFill, theme);
    // transparent (alpha=0) → effectively no fill
    if (color === 'transparent') return undefined;
    outline.color = color;
  }

  const prstDash = getEl(lnEl, A_NS, 'prstDash');
  if (prstDash) outline.dashStyle = attr(prstDash, 'val') || undefined;

  return outline.width || outline.color ? outline : undefined;
}

/* ------------------------------------------------------------------ */
/* _rels 파싱                                                           */
/* ------------------------------------------------------------------ */

function parseRels(xml: string): Map<string, string> {
  const dom = new DOMParser().parseFromString(xml, 'application/xml');
  const map = new Map<string, string>();
  const relEls = dom.getElementsByTagNameNS(REL_NS, 'Relationship');
  for (let i = 0; i < relEls.length; i++) {
    const el = relEls[i];
    const id = attr(el, 'Id');
    const target = attr(el, 'Target');
    if (id && target) map.set(id, target);
  }
  return map;
}

/* ------------------------------------------------------------------ */
/* 미디어 로드                                                          */
/* ------------------------------------------------------------------ */

/** Resolve a relative path against a base directory (handles ../) */
function resolvePath(base: string, rel: string): string {
  if (rel.startsWith('/')) return rel.slice(1);
  const parts = base.split('/');
  const relParts = rel.split('/');
  for (const seg of relParts) {
    if (seg === '..') parts.pop();
    else if (seg !== '.') parts.push(seg);
  }
  return parts.join('/');
}

async function loadMedia(
  zip: JSZip,
  slideRels: Map<string, Map<string, string>>,
): Promise<Map<string, string>> {
  const media = new Map<string, string>();
  const fileCache = new Map<string, string>();

  for (const [partPath, rels] of slideRels) {
    const dir = partPath.substring(0, partPath.lastIndexOf('/'));
    for (const [rId, target] of rels) {
      if (!target.includes('media/') && !target.endsWith('.png') && !target.endsWith('.jpg') &&
          !target.endsWith('.jpeg') && !target.endsWith('.gif') && !target.endsWith('.svg') &&
          !target.endsWith('.bmp') && !target.endsWith('.emf') && !target.endsWith('.wmf')) continue;

      const normalizedPath = resolvePath(dir, target);
      const key = `${partPath}::${rId}`;

      if (fileCache.has(normalizedPath)) {
        media.set(key, fileCache.get(normalizedPath)!);
        continue;
      }

      const file = zip.file(normalizedPath);
      if (!file) continue;

      const data = await file.async('base64');
      const ext = normalizedPath.split('.').pop()?.toLowerCase() || 'png';
      const mimeMap: Record<string, string> = {
        png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
        gif: 'image/gif', bmp: 'image/bmp', svg: 'image/svg+xml',
        emf: 'image/x-emf', wmf: 'image/x-wmf', tiff: 'image/tiff',
      };
      const mime = mimeMap[ext] || 'image/png';
      const dataUrl = `data:${mime};base64,${data}`;
      media.set(key, dataUrl);
      fileCache.set(normalizedPath, dataUrl);
    }
  }

  return media;
}

/* ------------------------------------------------------------------ */
/* TextBody 파싱                                                        */
/* ------------------------------------------------------------------ */

function mergeParagraphDefaults(
  primary?: PptxParagraphDefaults,
  fallback?: PptxParagraphDefaults,
): PptxParagraphDefaults | undefined {
  if (!primary && !fallback) return undefined;
  return {
    fontSize: primary?.fontSize ?? fallback?.fontSize,
    fontFamily: primary?.fontFamily ?? fallback?.fontFamily,
    bold: primary?.bold ?? fallback?.bold,
    italic: primary?.italic ?? fallback?.italic,
    color: primary?.color ?? fallback?.color,
  };
}

type PptxParagraphStyleDefaults = Pick<
  PptxParagraph,
  'alignment'
  | 'bulletType'
  | 'bulletChar'
  | 'bulletAutoNumType'
  | 'bulletFontFamily'
  | 'spacing'
  | 'indent'
  | 'defaultRunProps'
>;

function parseParagraphStyleDefaultsFromPPr(
  pPr: Element,
  theme: PptxTheme,
): PptxParagraphStyleDefaults {
  const defaults: PptxParagraphStyleDefaults = {};

  const algn = attr(pPr, 'algn');
  if (algn) defaults.alignment = algn;

  const indent = attr(pPr, 'indent');
  const marL = attr(pPr, 'marL');
  if (indent || marL) {
    const indentPx = indent ? emuToPx(parseInt(indent, 10)) : undefined;
    defaults.indent = {
      left: marL ? emuToPx(parseInt(marL, 10)) : undefined,
      hanging: indentPx !== undefined && indentPx < 0 ? Math.abs(indentPx) : undefined,
      firstLine: indentPx !== undefined && indentPx > 0 ? indentPx : undefined,
    };
  }

  const buNone = getEl(pPr, A_NS, 'buNone');
  const buChar = getEl(pPr, A_NS, 'buChar');
  const buAutoNum = getEl(pPr, A_NS, 'buAutoNum');
  const buFont = getEl(pPr, A_NS, 'buFont');
  if (buNone) {
    defaults.bulletType = 'none';
  } else if (buChar) {
    defaults.bulletType = 'char';
    defaults.bulletChar = attr(buChar, 'char') || '•';
  } else if (buAutoNum) {
    defaults.bulletType = 'autoNum';
    defaults.bulletAutoNumType = attr(buAutoNum, 'type') || 'arabicPeriod';
  }
  if (buFont) {
    const typeface = resolveThemeTypeface(attr(buFont, 'typeface'), theme);
    if (typeface) defaults.bulletFontFamily = typeface;
  }

  const spcBef = getEl(pPr, A_NS, 'spcBef');
  const spcAft = getEl(pPr, A_NS, 'spcAft');
  const lnSpc = getEl(pPr, A_NS, 'lnSpc');
  if (spcBef || spcAft || lnSpc) {
    defaults.spacing = {};
    if (spcBef) {
      const pts = getEl(spcBef, A_NS, 'spcPts');
      if (pts) defaults.spacing.before = parseInt(attr(pts, 'val') || '0', 10) / 100;
    }
    if (spcAft) {
      const pts = getEl(spcAft, A_NS, 'spcPts');
      if (pts) defaults.spacing.after = parseInt(attr(pts, 'val') || '0', 10) / 100;
    }
    if (lnSpc) {
      const pct = getEl(lnSpc, A_NS, 'spcPct');
      const pts = getEl(lnSpc, A_NS, 'spcPts');
      if (pct) {
        defaults.spacing.lineSpacing = parseInt(attr(pct, 'val') || '100000', 10) / 1000;
      } else if (pts) {
        defaults.spacing.lineSpacing = -(parseInt(attr(pts, 'val') || '0', 10) / 100);
      }
    }
  }

  const defRPr = getEl(pPr, A_NS, 'defRPr');
  if (defRPr) {
    defaults.defaultRunProps = parseDefRPr(defRPr, theme);
  }

  return defaults;
}

function mergeParagraphStyleDefaults(
  primary?: PptxParagraphStyleDefaults,
  fallback?: PptxParagraphStyleDefaults,
): PptxParagraphStyleDefaults | undefined {
  if (!primary && !fallback) return undefined;
  const spacing = {
    before: primary?.spacing?.before ?? fallback?.spacing?.before,
    after: primary?.spacing?.after ?? fallback?.spacing?.after,
    lineSpacing: primary?.spacing?.lineSpacing ?? fallback?.spacing?.lineSpacing,
  };
  const indent = {
    left: primary?.indent?.left ?? fallback?.indent?.left,
    hanging: primary?.indent?.hanging ?? fallback?.indent?.hanging,
    firstLine: primary?.indent?.firstLine ?? fallback?.indent?.firstLine,
  };
  return {
    alignment: primary?.alignment ?? fallback?.alignment,
    bulletType: primary?.bulletType ?? fallback?.bulletType,
    bulletChar: primary?.bulletChar ?? fallback?.bulletChar,
    bulletAutoNumType: primary?.bulletAutoNumType ?? fallback?.bulletAutoNumType,
    bulletFontFamily: primary?.bulletFontFamily ?? fallback?.bulletFontFamily,
    spacing: Object.values(spacing).some((value) => value !== undefined) ? spacing : undefined,
    indent: Object.values(indent).some((value) => value !== undefined) ? indent : undefined,
    defaultRunProps: mergeParagraphDefaults(primary?.defaultRunProps, fallback?.defaultRunProps),
  };
}

function parseListStyleDefaults(
  txBodyEl: Element,
  theme: PptxTheme,
): { defaultProps?: PptxParagraphStyleDefaults; levels: Map<number, PptxParagraphStyleDefaults> } {
  const levels = new Map<number, PptxParagraphStyleDefaults>();
  let defaultProps: PptxParagraphStyleDefaults | undefined;

  const lstStyle = getEl(txBodyEl, A_NS, 'lstStyle');
  if (!lstStyle) return { defaultProps, levels };

  const defPPr = getEl(lstStyle, A_NS, 'defPPr');
  if (defPPr) {
    defaultProps = parseParagraphStyleDefaultsFromPPr(defPPr, theme);
  }

  for (let lvl = 1; lvl <= 9; lvl++) {
    const lvlPPr = getEl(lstStyle, A_NS, `lvl${lvl}pPr`);
    if (!lvlPPr) continue;
    levels.set(lvl - 1, parseParagraphStyleDefaultsFromPPr(lvlPPr, theme));
  }

  return { defaultProps, levels };
}

function parseTextBody(
  txBodyEl: Element,
  theme: PptxTheme,
  inheritedDefaults?: PptxParagraphDefaults,
): PptxTextBody {
  const bodyPrEl = getEl(txBodyEl, A_NS, 'bodyPr');
  const bodyProps: PptxBodyProps = {};
  if (bodyPrEl) {
    bodyProps.anchor = attr(bodyPrEl, 'anchor') || undefined;
    bodyProps.anchorCtr = attr(bodyPrEl, 'anchorCtr') === '1' || attr(bodyPrEl, 'anchorCtr') === 'true';
    bodyProps.wrap = attr(bodyPrEl, 'wrap') || undefined;
    bodyProps.horzOverflow = attr(bodyPrEl, 'horzOverflow') || undefined;
    bodyProps.vertOverflow = attr(bodyPrEl, 'vertOverflow') || undefined;
    const lIns = attr(bodyPrEl, 'lIns');
    const tIns = attr(bodyPrEl, 'tIns');
    const rIns = attr(bodyPrEl, 'rIns');
    const bIns = attr(bodyPrEl, 'bIns');
    if (lIns) bodyProps.lIns = emuToPx(parseInt(lIns, 10));
    if (tIns) bodyProps.tIns = emuToPx(parseInt(tIns, 10));
    if (rIns) bodyProps.rIns = emuToPx(parseInt(rIns, 10));
    if (bIns) bodyProps.bIns = emuToPx(parseInt(bIns, 10));

    const normAutofit = getEl(bodyPrEl, A_NS, 'normAutofit');
    const spAutoFit = getEl(bodyPrEl, A_NS, 'spAutoFit');
    if (normAutofit) bodyProps.autofit = 'shrink';
    else if (spAutoFit) bodyProps.autofit = 'normal';
    else bodyProps.autofit = 'none';
  }

  const paragraphs: PptxParagraph[] = [];
  const listStyleDefaults = parseListStyleDefaults(txBodyEl, theme);
  const pEls = directChildrenNS(txBodyEl, A_NS, 'p');
  for (const pEl of pEls) {
    const para = parseParagraph(pEl, theme);
    const lvl = para.level ?? 0;
    const listDefaults = mergeParagraphStyleDefaults(
      listStyleDefaults.levels.get(lvl),
      listStyleDefaults.defaultProps,
    );
    mergeParagraphFormatting(para, listDefaults);
    para.defaultRunProps = mergeParagraphDefaults(para.defaultRunProps, inheritedDefaults);
    paragraphs.push(para);
  }

  return { paragraphs, bodyProps };
}

function parseDefRPr(defRPrEl: Element, theme: PptxTheme): PptxParagraphDefaults {
  const defaults: PptxParagraphDefaults = {};
  const sz = attr(defRPrEl, 'sz');
  if (sz) defaults.fontSize = parseInt(sz, 10) / 100;
  const b = attr(defRPrEl, 'b');
  if (b === '1' || b === 'true') defaults.bold = true;
  const i = attr(defRPrEl, 'i');
  if (i === '1' || i === 'true') defaults.italic = true;
  const solidFill = directChildrenNS(defRPrEl, A_NS, 'solidFill')[0];
  if (solidFill) defaults.color = parseColor(solidFill, theme);
  const latin = getEl(defRPrEl, A_NS, 'latin');
  if (latin) {
    const typeface = resolveThemeTypeface(attr(latin, 'typeface'), theme);
    if (typeface) defaults.fontFamily = typeface;
  }
  return defaults;
}

function parseParagraph(pEl: Element, theme: PptxTheme): PptxParagraph {
  const para: PptxParagraph = { id: genId(), runs: [] };

  const pPr = getEl(pEl, A_NS, 'pPr');
  if (pPr) {
    const lvl = attr(pPr, 'lvl');
    if (lvl) para.level = parseInt(lvl, 10);
    mergeParagraphFormatting(para, parseParagraphStyleDefaultsFromPPr(pPr, theme));
  }

  const endParaRPr = getEl(pEl, A_NS, 'endParaRPr');
  if (endParaRPr) {
    para.defaultRunProps = mergeParagraphDefaults(
      para.defaultRunProps,
      parseDefRPr(endParaRPr, theme),
    );
  }

  for (let i = 0; i < pEl.childNodes.length; i++) {
    const child = pEl.childNodes[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;

    if (el.localName === 'r' && el.namespaceURI === A_NS) {
      para.runs.push(parseRun(el, theme));
    } else if (el.localName === 'br' && el.namespaceURI === A_NS) {
      para.runs.push({ id: genId(), type: 'break' });
    } else if (el.localName === 'fld' && el.namespaceURI === A_NS) {
      const t = getEl(el, A_NS, 't');
      para.runs.push({ id: genId(), type: 'field', text: t?.textContent || '' });
    }
  }

  return para;
}

function parseRun(rEl: Element, theme: PptxTheme): PptxRun {
  const run: PptxRun = { id: genId(), type: 'text' };

  const rPr = getEl(rEl, A_NS, 'rPr');
  if (rPr) {
    const b = attr(rPr, 'b');
    if (b === '1' || b === 'true') run.bold = true;

    const i = attr(rPr, 'i');
    if (i === '1' || i === 'true') run.italic = true;

    const u = attr(rPr, 'u');
    if (u && u !== 'none') run.underline = true;

    const strike = attr(rPr, 'strike');
    if (strike && strike !== 'noStrike') run.strike = true;

    const sz = attr(rPr, 'sz');
    if (sz) run.fontSize = parseInt(sz, 10) / 100;

    // directChildrenNS 사용: a:ln 등 자식 엘리먼트 내부의 solidFill 오인식 방지
    const solidFill = directChildrenNS(rPr, A_NS, 'solidFill')[0];
    if (solidFill) run.color = parseColor(solidFill, theme);

    const latin = getEl(rPr, A_NS, 'latin');
    if (latin) {
      const typeface = resolveThemeTypeface(attr(latin, 'typeface'), theme);
      if (typeface) run.fontFamily = typeface;
    }

    const ea = getEl(rPr, A_NS, 'ea');
    if (ea && !run.fontFamily) {
      const typeface = resolveThemeTypeface(attr(ea, 'typeface'), theme);
      if (typeface) run.fontFamily = typeface;
    }

    const hlinkClr = getEl(rPr, A_NS, 'highlight');
    if (hlinkClr) run.highlight = parseColor(hlinkClr, theme);
  }

  const t = getEl(rEl, A_NS, 't');
  if (t) run.text = t.textContent || '';

  return run;
}

/* ------------------------------------------------------------------ */
/* 도형(Shape) 파싱                                                     */
/* ------------------------------------------------------------------ */

function parseTransform(spPrEl: Element): {
  pos: { x: number; y: number };
  size: { width: number; height: number };
  rotation?: number;
  flipH?: boolean;
  flipV?: boolean;
} | null {
  const xfrm = getEl(spPrEl, A_NS, 'xfrm');
  if (!xfrm) return null;

  const off = getEl(xfrm, A_NS, 'off');
  const ext = getEl(xfrm, A_NS, 'ext');
  if (!off || !ext) return null;

  const x = parseInt(attr(off, 'x') || '0', 10);
  const y = parseInt(attr(off, 'y') || '0', 10);
  const cx = parseInt(attr(ext, 'cx') || '0', 10);
  const cy = parseInt(attr(ext, 'cy') || '0', 10);

  const rot = attr(xfrm, 'rot');
  const rotation = rot ? parseInt(rot, 10) / 60000 : undefined;

  const flipHVal = attr(xfrm, 'flipH');
  const flipVVal = attr(xfrm, 'flipV');
  const flipH = flipHVal === '1' || flipHVal === 'true' ? true : undefined;
  const flipV = flipVVal === '1' || flipVVal === 'true' ? true : undefined;

  return {
    pos: { x: emuToPx(x), y: emuToPx(y) },
    size: { width: emuToPx(cx), height: emuToPx(cy) },
    rotation,
    flipH,
    flipV,
  };
}

function parseGeometry(spPrEl: Element): PptxGeometry | undefined {
  const prstGeom = getEl(spPrEl, A_NS, 'prstGeom');
  if (prstGeom) {
    return { preset: attr(prstGeom, 'prst') || 'rect' };
  }
  const custGeom = getEl(spPrEl, A_NS, 'custGeom');
  if (custGeom) {
    return { preset: 'custom' };
  }
  return undefined;
}

interface PptxShapeStyleDefaults {
  textDefaults?: PptxParagraphDefaults;
  fill?: PptxFill;
  outline?: PptxOutline;
}

function parseShapeStyleDefaults(spEl: Element, theme: PptxTheme): PptxShapeStyleDefaults | undefined {
  const styleEl = getEl(spEl, P_NS, 'style');
  if (!styleEl) return undefined;

  const fontRef = getEl(styleEl, A_NS, 'fontRef');
  const styleDefaults: PptxShapeStyleDefaults = {};

  if (fontRef) {
    const defaults: PptxParagraphDefaults = {};
    const idx = attr(fontRef, 'idx');
    if (idx === 'major') defaults.fontFamily = theme.fonts.major;
    if (idx === 'minor') defaults.fontFamily = theme.fonts.minor;

    const color = parseColor(fontRef, theme);
    if (color) defaults.color = color;

    if (Object.keys(defaults).length > 0) {
      styleDefaults.textDefaults = defaults;
    }
  }

  const fillRef = getEl(styleEl, A_NS, 'fillRef');
  if (fillRef) {
    const color = parseColor(fillRef, theme);
    if (color) {
      styleDefaults.fill = { type: 'solid', color };
    }
  }

  const lnRef = getEl(styleEl, A_NS, 'lnRef');
  if (lnRef) {
    const idx = parseInt(attr(lnRef, 'idx') || '1', 10);
    const outline: PptxOutline = idx > 0 && idx <= theme.lineStyles.length
      ? { ...theme.lineStyles[idx - 1] }
      : {};
    const color = parseColor(lnRef, theme);
    if (color) outline.color = color;
    if (!outline.width && outline.color) outline.width = 1;
    if (outline.width || outline.color || outline.dashStyle) {
      styleDefaults.outline = outline;
    }
  }

  return Object.keys(styleDefaults).length > 0 ? styleDefaults : undefined;
}

function parseShape(spEl: Element, theme: PptxTheme): PptxShape | null {
  const nvSpPr = getEl(spEl, P_NS, 'nvSpPr') || getEl(spEl, P_NS, 'nvCxnSpPr');
  const cNvPr = nvSpPr ? getEl(nvSpPr, P_NS, 'cNvPr') : null;
  const shapeName = cNvPr ? (attr(cNvPr, 'name') || '') : '';
  const shapeId = cNvPr ? (attr(cNvPr, 'id') || genId()) : genId();

  const spPr = getEl(spEl, P_NS, 'spPr');
  if (!spPr) return null;

  let placeholder: PptxShape['placeholder'];
  if (nvSpPr) {
    const nvPr = getEl(nvSpPr, P_NS, 'nvPr');
    if (nvPr) {
      const ph = getEl(nvPr, P_NS, 'ph');
      if (ph) {
        placeholder = {
          type: attr(ph, 'type') || 'body',
          idx: attr(ph, 'idx') || undefined,
        };
      }
    }
  }

  const transform = parseTransform(spPr);
  // Placeholders without explicit xfrm inherit from layout; use a default
  if (!transform && !placeholder) return null;

  const pos = transform?.pos ?? { x: 0, y: 0 };
  const size = transform?.size ?? { width: 0, height: 0 };
  const rotation = transform?.rotation;
  const flipH = transform?.flipH;
  const flipV = transform?.flipV;

  const isConnector = spEl.localName === 'cxnSp';
  const hasTextBody = !!getEl(spEl, P_NS, 'txBody');
  const geometry = parseGeometry(spPr);
  const hasGeometry = !!geometry && geometry.preset !== 'rect';
  const styleDefaults = parseShapeStyleDefaults(spEl, theme);

  const fillVal = parseFill(spPr, theme);
  const lnEl = getEl(spPr, A_NS, 'ln');
  const outlineVal = lnEl ? parseOutline(lnEl, theme) : undefined;
  const effectiveFill = fillVal ?? styleDefaults?.fill;
  const effectiveOutline = outlineVal ?? styleDefaults?.outline;
  const hasVisualDecoration = !!(effectiveFill && effectiveFill.type !== 'none') || !!effectiveOutline;

  let type: PptxShape['type'] = 'shape';
  if (isConnector) type = 'connector';
  else if (hasTextBody && !hasGeometry && !placeholder && !hasVisualDecoration) type = 'textbox';
  else if (hasTextBody || hasGeometry || placeholder || hasVisualDecoration) type = 'shape';

  const shape: PptxShape = {
    id: `sp_${shapeId}`,
    name: shapeName,
    type,
    position: pos,
    size,
    hasExplicitTransform: !!transform,
    rotation,
    flipH,
    flipV,
    geometry: geometry ?? { preset: 'rect' },
    placeholder,
  };

  shape.fill = effectiveFill;
  shape.outline = effectiveOutline;

  const txBody = getEl(spEl, P_NS, 'txBody');
  if (txBody) {
    const inheritedDefaults = styleDefaults?.textDefaults;
    shape.textBody = parseTextBody(txBody, theme, inheritedDefaults);
  }

  return shape;
}

function parsePicture(picEl: Element, theme: PptxTheme): PptxShape | null {
  const nvPicPr = getEl(picEl, P_NS, 'nvPicPr');
  const cNvPr = nvPicPr ? getEl(nvPicPr, P_NS, 'cNvPr') : null;
  const shapeName = cNvPr ? (attr(cNvPr, 'name') || '') : '';
  const shapeId = cNvPr ? (attr(cNvPr, 'id') || genId()) : genId();

  const spPr = getEl(picEl, P_NS, 'spPr');
  if (!spPr) return null;

  const transform = parseTransform(spPr);
  if (!transform) return null;

  const blipFill = getEl(picEl, P_NS, 'blipFill');
  let imageRId: string | undefined;
  let imageCrop: PptxImageCrop | undefined;
  if (blipFill) {
    const blip = getEl(blipFill, A_NS, 'blip');
    if (blip) {
      imageRId = blip.getAttributeNS(R_NS, 'embed') || attr(blip, 'r:embed') || undefined;
    }
    const srcRect = getEl(blipFill, A_NS, 'srcRect');
    if (srcRect) {
      const l = parseInt(attr(srcRect, 'l') || '0', 10);
      const t = parseInt(attr(srcRect, 't') || '0', 10);
      const r = parseInt(attr(srcRect, 'r') || '0', 10);
      const b = parseInt(attr(srcRect, 'b') || '0', 10);
      if (l !== 0 || t !== 0 || r !== 0 || b !== 0) {
        imageCrop = { left: l, top: t, right: r, bottom: b };
      }
    }
  }

  return {
    id: `pic_${shapeId}`,
    name: shapeName,
    type: 'image',
    position: transform.pos,
    size: transform.size,
    hasExplicitTransform: true,
    rotation: transform.rotation,
    flipH: transform.flipH,
    flipV: transform.flipV,
    imageRId,
    imageCrop,
    fill: parseFill(spPr, theme),
  };
}

function parseGroupShape(grpSpEl: Element, theme: PptxTheme): PptxShape | null {
  const nvGrpSpPr = getEl(grpSpEl, P_NS, 'nvGrpSpPr');
  const cNvPr = nvGrpSpPr ? getEl(nvGrpSpPr, P_NS, 'cNvPr') : null;
  const shapeName = cNvPr ? (attr(cNvPr, 'name') || '') : '';
  const shapeId = cNvPr ? (attr(cNvPr, 'id') || genId()) : genId();

  const grpSpPr = getEl(grpSpEl, P_NS, 'grpSpPr');
  if (!grpSpPr) return null;

  const xfrm = getEl(grpSpPr, A_NS, 'xfrm');
  if (!xfrm) return null;

  const off = getEl(xfrm, A_NS, 'off');
  const ext = getEl(xfrm, A_NS, 'ext');
  if (!off || !ext) return null;

  const chOff = getEl(xfrm, A_NS, 'chOff');
  const chExt = getEl(xfrm, A_NS, 'chExt');

  const children = parseShapeTree(grpSpEl, theme);

  const shape: PptxShape = {
    id: `grp_${shapeId}`,
    name: shapeName,
    type: 'group',
    position: { x: emuToPx(parseInt(attr(off, 'x') || '0', 10)), y: emuToPx(parseInt(attr(off, 'y') || '0', 10)) },
    size: { width: emuToPx(parseInt(attr(ext, 'cx') || '0', 10)), height: emuToPx(parseInt(attr(ext, 'cy') || '0', 10)) },
    hasExplicitTransform: true,
    children,
  };

  if (chOff) {
    shape.groupChildOffset = {
      x: emuToPx(parseInt(attr(chOff, 'x') || '0', 10)),
      y: emuToPx(parseInt(attr(chOff, 'y') || '0', 10)),
    };
  }
  if (chExt) {
    shape.groupChildExtent = {
      width: emuToPx(parseInt(attr(chExt, 'cx') || '0', 10)),
      height: emuToPx(parseInt(attr(chExt, 'cy') || '0', 10)),
    };
  }

  return shape;
}

function parseGraphicFrame(gfEl: Element, theme: PptxTheme): PptxShape | null {
  const nvGraphicFramePr = getEl(gfEl, P_NS, 'nvGraphicFramePr');
  const cNvPr = nvGraphicFramePr ? getEl(nvGraphicFramePr, P_NS, 'cNvPr') : null;
  const shapeName = cNvPr ? (attr(cNvPr, 'name') || '') : '';
  const shapeId = cNvPr ? (attr(cNvPr, 'id') || genId()) : genId();

  // graphicFrame uses p:xfrm (P_NS) containing a:off/a:ext (A_NS)
  const xfrm = directChildrenNS(gfEl, P_NS, 'xfrm')[0]
    || getEl(gfEl, P_NS, 'xfrm')
    || getEl(gfEl, A_NS, 'xfrm');

  let pos = { x: 0, y: 0 };
  let size = { width: 0, height: 0 };

  if (xfrm) {
    const off = getEl(xfrm, A_NS, 'off') || xfrm.querySelector('off');
    const ext = getEl(xfrm, A_NS, 'ext') || xfrm.querySelector('ext');
    if (off) {
      pos = { x: emuToPx(parseInt(attr(off, 'x') || '0', 10)), y: emuToPx(parseInt(attr(off, 'y') || '0', 10)) };
    }
    if (ext) {
      size = { width: emuToPx(parseInt(attr(ext, 'cx') || '0', 10)), height: emuToPx(parseInt(attr(ext, 'cy') || '0', 10)) };
    }
  }

  const graphic = gfEl.getElementsByTagNameNS(A_NS, 'graphic')[0];
  if (!graphic) return null;

  const graphicData = getEl(graphic, A_NS, 'graphicData');
  if (!graphicData) return null;

  const tblEl = graphicData.getElementsByTagNameNS(A_NS, 'tbl')[0];
  if (tblEl) {
    const tableGrid = parseTable(tblEl, theme);

    // PowerPoint는 실제 테이블 크기로 그리드 컬럼/행 치수를 사용함.
    // Google Slides 내보내기에서 frame cx/cy가 grid 합계와 다를 때 grid를 우선 사용.
    const gridWidth = tableGrid.gridCols.reduce((s, w) => s + w, 0);
    const gridHeight = tableGrid.rows.reduce((s, r) => s + r.height, 0);
    const actualSize = {
      width: gridWidth > 0 ? gridWidth : size.width,
      height: gridHeight > 0 ? gridHeight : size.height,
    };

    return {
      id: `tbl_${shapeId}`,
      name: shapeName,
      type: 'table',
      position: pos,
      size: actualSize,
      tableGrid,
    };
  }

  return null;
}

function parseTable(tblEl: Element, theme: PptxTheme): PptxTableGrid {
  const gridCols: number[] = [];
  const tblGrid = getEl(tblEl, A_NS, 'tblGrid');
  if (tblGrid) {
    const gridColEls = directChildrenNS(tblGrid, A_NS, 'gridCol');
    for (const gc of gridColEls) {
      const w = attr(gc, 'w');
      gridCols.push(w ? emuToPx(parseInt(w, 10)) : 100);
    }
  }

  const rows: PptxTableRow[] = [];
  const trEls = directChildrenNS(tblEl, A_NS, 'tr');
  for (const trEl of trEls) {
    const h = attr(trEl, 'h');
    const row: PptxTableRow = {
      height: h ? emuToPx(parseInt(h, 10)) : 30,
      cells: [],
    };

    const tcEls = directChildrenNS(trEl, A_NS, 'tc');
    for (const tcEl of tcEls) {
      const cell: PptxTableCell = {};
      const tcPr = getEl(tcEl, A_NS, 'tcPr');

      const gridSpan = attr(tcEl, 'gridSpan') || (tcPr ? attr(tcPr, 'gridSpan') : null);
      if (gridSpan) cell.colSpan = parseInt(gridSpan, 10);

      const rowSpan = attr(tcEl, 'rowSpan') || (tcPr ? attr(tcPr, 'rowSpan') : null);
      if (rowSpan) cell.rowSpan = parseInt(rowSpan, 10);

      const hMerge = attr(tcEl, 'hMerge') || (tcPr ? attr(tcPr, 'hMerge') : null);
      if (hMerge === '1' || hMerge === 'true') cell.hMerge = true;

      const vMerge = attr(tcEl, 'vMerge') || (tcPr ? attr(tcPr, 'vMerge') : null);
      if (vMerge === '1' || vMerge === 'true') cell.vMerge = true;

      if (tcPr) {
        cell.fill = parseFill(tcPr, theme);

        // 셀 내부 패딩 (marL/R/T/B → EMU → px), 기본값 PPTX 표준 91440 EMU = ~9.6px
        const defaultPadPx = emuToPx(91440);
        const marL = attr(tcPr, 'marL');
        const marR = attr(tcPr, 'marR');
        const marT = attr(tcPr, 'marT');
        const marB = attr(tcPr, 'marB');
        cell.padding = {
          left:   marL ? emuToPx(parseInt(marL, 10)) : defaultPadPx,
          right:  marR ? emuToPx(parseInt(marR, 10)) : defaultPadPx,
          top:    marT ? emuToPx(parseInt(marT, 10)) : defaultPadPx,
          bottom: marB ? emuToPx(parseInt(marB, 10)) : defaultPadPx,
        };

        // 수직 정렬 anchor: t(top) / ctr(center) / b(bottom)
        const anchor = attr(tcPr, 'anchor');
        if (anchor) cell.anchor = anchor;

        const borders: PptxTableCell['borders'] = {};
        const sides = ['lnL', 'lnR', 'lnT', 'lnB'] as const;
        const sideMap = { lnL: 'left', lnR: 'right', lnT: 'top', lnB: 'bottom' } as const;
        for (const side of sides) {
          const lnEl = getEl(tcPr, A_NS, side);
          if (lnEl) {
            // noFill → 테두리 없음 처리
            const noFill = getEl(lnEl, A_NS, 'noFill');
            if (noFill) continue;
            const border: PptxBorder = {};
            const w = attr(lnEl, 'w');
            if (w) border.width = emuToPx(parseInt(w, 10)) || 1;
            const sf = directChildrenNS(lnEl, A_NS, 'solidFill')[0];
            if (sf) {
              const borderColor = parseColor(sf, theme);
              // alpha=0 → 투명 → 테두리 없음
              if (borderColor === 'transparent') continue;
              border.color = borderColor;
            }
            borders[sideMap[side]] = border;
          }
        }
        if (Object.keys(borders).length > 0) cell.borders = borders;
      }

      const txBody = getEl(tcEl, A_NS, 'txBody');
      if (txBody) cell.textBody = parseTextBody(txBody, theme);

      row.cells.push(cell);
    }

    rows.push(row);
  }

  return { rows, gridCols };
}

/* ------------------------------------------------------------------ */
/* ShapeTree 파싱                                                       */
/* ------------------------------------------------------------------ */

function parseShapeTree(parentEl: Element, theme: PptxTheme): PptxShape[] {
  const shapes: PptxShape[] = [];

  for (let i = 0; i < parentEl.childNodes.length; i++) {
    const child = parentEl.childNodes[i];
    if (child.nodeType !== 1) continue;
    const el = child as Element;

    if (el.localName === 'sp' && el.namespaceURI === P_NS) {
      const shape = parseShape(el, theme);
      if (shape) shapes.push(shape);
    } else if (el.localName === 'pic' && el.namespaceURI === P_NS) {
      const pic = parsePicture(el, theme);
      if (pic) shapes.push(pic);
    } else if (el.localName === 'grpSp' && el.namespaceURI === P_NS) {
      const grp = parseGroupShape(el, theme);
      if (grp) shapes.push(grp);
    } else if (el.localName === 'graphicFrame' && el.namespaceURI === P_NS) {
      const gf = parseGraphicFrame(el, theme);
      if (gf) shapes.push(gf);
    } else if (el.localName === 'cxnSp' && el.namespaceURI === P_NS) {
      const conn = parseShape(el, theme);
      if (conn) shapes.push(conn);
    }
  }

  return shapes;
}

/* ------------------------------------------------------------------ */
/* Background 파싱                                                      */
/* ------------------------------------------------------------------ */

function parseBackground(bgEl: Element | null, theme: PptxTheme): PptxBackground | undefined {
  if (!bgEl) return undefined;

  // bgPr contains an explicit fill
  const bgPr = getEl(bgEl, P_NS, 'bgPr');
  if (bgPr) {
    const fill = parseFill(bgPr, theme);
    return fill ? { fill } : undefined;
  }

  // bgRef references a theme fill style by index
  const bgRef = getEl(bgEl, P_NS, 'bgRef');
  if (bgRef) {
    const color = parseColor(bgRef, theme);
    if (color) return { fill: { type: 'solid', color } };
    // idx="1001" etc. often maps to lt1 or a subtle fill
    const idx = attr(bgRef, 'idx');
    if (idx) {
      const idxNum = parseInt(idx, 10);
      if (idxNum >= 1000) {
        return { fill: { type: 'solid', color: theme.colors.lt1 } };
      }
    }
  }

  return undefined;
}

/* ------------------------------------------------------------------ */
/* 메인 파서                                                             */
/* ------------------------------------------------------------------ */

export async function parsePptx(buffer: ArrayBuffer): Promise<PptxPresentation> {
  _idCounter = 0;
  const zip = await JSZip.loadAsync(buffer);
  const rawParts = new Map<string, string>();

  const presXml = await readPart(zip, 'ppt/presentation.xml');
  if (!presXml) throw new Error('ppt/presentation.xml을 찾을 수 없습니다.');
  rawParts.set('ppt/presentation.xml', presXml);
  const presDom = new DOMParser().parseFromString(presXml, 'application/xml');

  const presRelsXml = await readPart(zip, 'ppt/_rels/presentation.xml.rels');
  const presRels = presRelsXml ? parseRels(presRelsXml) : new Map<string, string>();

  const themeXml = await findAndReadTheme(zip, presRels);
  const theme = themeXml
    ? parseTheme(themeXml)
    : {
        ...DEFAULT_THEME,
        colors: { ...DEFAULT_THEME.colors },
        fonts: { ...DEFAULT_THEME.fonts },
        colorMap: { ...DEFAULT_THEME.colorMap },
        lineStyles: DEFAULT_THEME.lineStyles.map((line) => ({ ...line })),
      };

  const slideSizeEl = getEl(presDom.documentElement, P_NS, 'sldSz');
  const slideSize = {
    width: slideSizeEl ? emuToPx(parseInt(attr(slideSizeEl, 'cx') || '9144000', 10)) : emuToPx(9144000),
    height: slideSizeEl ? emuToPx(parseInt(attr(slideSizeEl, 'cy') || '6858000', 10)) : emuToPx(6858000),
  };

  const sldIdLstEl = getEl(presDom.documentElement, P_NS, 'sldIdLst');
  const slideRIds: string[] = [];
  if (sldIdLstEl) {
    const sldIdEls = directChildrenNS(sldIdLstEl, P_NS, 'sldId');
    for (const sldIdEl of sldIdEls) {
      const rId = sldIdEl.getAttributeNS(R_NS, 'id') || attr(sldIdEl, 'r:id');
      if (rId) slideRIds.push(rId);
    }
  }

  const slidePartPaths: string[] = [];
  for (const rId of slideRIds) {
    const target = presRels.get(rId);
    if (target) {
      const path = target.startsWith('/') ? target.slice(1) : `ppt/${target}`;
      slidePartPaths.push(path);
    }
  }

  const slideMasters = await parseSlideMasters(zip, presRels, theme, rawParts);
  const slideLayouts = await parseSlideLayouts(zip, presRels, slideMasters, theme, rawParts);

  const allSlideRels = new Map<string, Map<string, string>>();
  const slides: PptxSlide[] = [];

  for (let idx = 0; idx < slidePartPaths.length; idx++) {
    const partPath = slidePartPaths[idx];
    const slideXml = await readPart(zip, partPath);
    if (!slideXml) continue;
    rawParts.set(partPath, slideXml);

    const dir = partPath.substring(0, partPath.lastIndexOf('/'));
    const fileName = partPath.substring(partPath.lastIndexOf('/') + 1);
    const slideRelsPath = `${dir}/_rels/${fileName}.rels`;
    const slideRelsXml = await readPart(zip, slideRelsPath);
    const slideRels = slideRelsXml ? parseRels(slideRelsXml) : new Map<string, string>();
    allSlideRels.set(partPath, slideRels);

    let layoutRef: string | undefined;
    for (const [, target] of slideRels) {
      if (target.includes('slideLayout')) {
        layoutRef = resolvePath(dir, target);
        break;
      }
    }

    const layout = layoutRef
      ? slideLayouts.find((candidate) => candidate.partPath === layoutRef)
      : undefined;

    const slideDom = new DOMParser().parseFromString(slideXml, 'application/xml');
    const baseColorMap = layout?.colorMap || theme.colorMap;
    const colorOverride = parseColorMapOverride(slideDom.documentElement);
    const colorMap = colorOverride === 'master'
      ? baseColorMap
      : mergeColorMap(baseColorMap, colorOverride);
    const slideTheme = withColorMap(theme, colorMap);

    const cSld = getEl(slideDom.documentElement, P_NS, 'cSld');
    if (!cSld) continue;

    const spTree = getEl(cSld, P_NS, 'spTree');
    const shapes = spTree ? parseShapeTree(spTree, slideTheme) : [];

    const bgEl = getEl(cSld, P_NS, 'bg');
    const background = parseBackground(bgEl, slideTheme);

    slides.push({
      id: `slide_${idx + 1}`,
      index: idx,
      partPath,
      shapes,
      background,
      layoutRef,
      colorMap,
    });
  }

  const media = await loadMedia(zip, allSlideRels);

  // Resolve placeholder transforms from layouts/masters
  for (const slide of slides) {
    resolvePlaceholderTransforms(slide, slideLayouts, slideMasters);
  }

  // 전체 슬라이드에서 실제 사용된 폰트 수집
  const usedFontSet = new Set<string>();
  for (const slide of slides) {
    for (const shape of slide.shapes) {
      collectShapeFonts(shape, usedFontSet);
    }
  }

  return {
    slides,
    slideSize,
    theme,
    slideMasters,
    slideLayouts,
    media,
    rawParts,
    zip,
    usedFonts: Array.from(usedFontSet),
  };
}

function collectShapeFonts(shape: PptxShape, fonts: Set<string>): void {
  const textBody = shape.textBody;
  if (textBody) {
    for (const para of textBody.paragraphs) {
      for (const run of para.runs) {
        if (run.fontFamily) fonts.add(run.fontFamily);
      }
      if (para.defaultRunProps?.fontFamily) fonts.add(para.defaultRunProps.fontFamily);
      if (para.bulletFontFamily) fonts.add(para.bulletFontFamily);
    }
  }
  if (shape.type === 'table' && shape.tableGrid) {
    for (const row of shape.tableGrid.rows) {
      for (const cell of row.cells) {
        if (cell.textBody) {
          for (const para of cell.textBody.paragraphs) {
            for (const run of para.runs) {
              if (run.fontFamily) fonts.add(run.fontFamily);
            }
            if (para.defaultRunProps?.fontFamily) fonts.add(para.defaultRunProps.fontFamily);
            if (para.bulletFontFamily) fonts.add(para.bulletFontFamily);
          }
        }
      }
    }
  }
}

function resolvePlaceholderTransforms(
  slide: PptxSlide,
  layouts: PptxSlideLayout[],
  masters: PptxSlideMaster[],
): void {
  const layout = slide.layoutRef
    ? layouts.find(l => l.partPath === slide.layoutRef)
    : undefined;
  const master = layout?.masterRef
    ? masters.find((candidate) => candidate.partPath === layout.masterRef)
    : masters[0];

  for (const shape of slide.shapes) {
    if (!shape.placeholder) continue;
    const match = findPlaceholderInSources(shape.placeholder, layout?.shapes, master?.shapes);
    if (!match) continue;

    if (!shape.hasExplicitTransform) {
      if (shape.size.width === 0) shape.size.width = match.size.width;
      if (shape.size.height === 0) shape.size.height = match.size.height;
      shape.position = { ...match.position };
    }

    inheritPlaceholderTextDefaults(shape, match);
  }
}

function findPlaceholderInSources(
  ph: NonNullable<PptxShape['placeholder']>,
  ...sources: (PptxShape[] | undefined)[]
): PptxShape | undefined {
  let fallbackMatch: PptxShape | undefined;
  for (const shapes of sources) {
    if (!shapes) continue;
    // Match by type + idx first, then by type alone
    const byTypeAndIdx = shapes.find(s =>
      s.placeholder?.type === ph.type && s.placeholder?.idx === ph.idx
    );
    if (byTypeAndIdx) {
      if (byTypeAndIdx.size.width > 0 || byTypeAndIdx.size.height > 0) return byTypeAndIdx;
      fallbackMatch ??= byTypeAndIdx;
    }

    const byType = shapes.find(s => s.placeholder?.type === ph.type);
    if (byType) {
      if (byType.size.width > 0 || byType.size.height > 0) return byType;
      fallbackMatch ??= byType;
    }
  }
  return fallbackMatch;
}

function mergeBodyProps(primary: PptxBodyProps, fallback: PptxBodyProps): PptxBodyProps {
  return {
    anchor: primary.anchor ?? fallback.anchor,
    anchorCtr: primary.anchorCtr ?? fallback.anchorCtr,
    wrap: primary.wrap ?? fallback.wrap,
    horzOverflow: primary.horzOverflow ?? fallback.horzOverflow,
    vertOverflow: primary.vertOverflow ?? fallback.vertOverflow,
    lIns: primary.lIns ?? fallback.lIns,
    tIns: primary.tIns ?? fallback.tIns,
    rIns: primary.rIns ?? fallback.rIns,
    bIns: primary.bIns ?? fallback.bIns,
    autofit: primary.autofit ?? fallback.autofit,
  };
}

function mergeParagraphFormatting(
  target: PptxParagraph,
  source?: PptxParagraphStyleDefaults | PptxParagraph,
): void {
  if (!source) return;

  target.alignment = target.alignment ?? source.alignment;
  target.bulletType = target.bulletType ?? source.bulletType;
  target.bulletChar = target.bulletChar ?? source.bulletChar;
  target.bulletAutoNumType = target.bulletAutoNumType ?? source.bulletAutoNumType;
  target.bulletFontFamily = target.bulletFontFamily ?? source.bulletFontFamily;

  if (source.spacing) {
    target.spacing = {
      before: target.spacing?.before ?? source.spacing.before,
      after: target.spacing?.after ?? source.spacing.after,
      lineSpacing: target.spacing?.lineSpacing ?? source.spacing.lineSpacing,
    };
  }

  if (source.indent) {
    target.indent = {
      left: target.indent?.left ?? source.indent.left,
      hanging: target.indent?.hanging ?? source.indent.hanging,
      firstLine: target.indent?.firstLine ?? source.indent.firstLine,
    };
  }

  target.defaultRunProps = mergeParagraphDefaults(target.defaultRunProps, source.defaultRunProps);
}

function inheritPlaceholderTextDefaults(target: PptxShape, source: PptxShape): void {
  if (!target.textBody || !source.textBody) return;

  target.textBody.bodyProps = mergeBodyProps(target.textBody.bodyProps, source.textBody.bodyProps);

  const sourceParagraphsByLevel = new Map<number, PptxParagraph>();
  for (const para of source.textBody.paragraphs) {
    const level = para.level ?? 0;
    if (!sourceParagraphsByLevel.has(level)) {
      sourceParagraphsByLevel.set(level, para);
    }
  }

  const fallbackParagraph =
    sourceParagraphsByLevel.get(0)
    || source.textBody.paragraphs[0];
  if (!fallbackParagraph && sourceParagraphsByLevel.size === 0) return;

  for (const para of target.textBody.paragraphs) {
    const byLevel = sourceParagraphsByLevel.get(para.level ?? 0);
    mergeParagraphFormatting(para, byLevel || fallbackParagraph);
  }
}

async function findAndReadTheme(zip: JSZip, presRels: Map<string, string>): Promise<string | null> {
  for (const [, target] of presRels) {
    if (target.includes('theme')) {
      const path = target.startsWith('/') ? target.slice(1) : `ppt/${target}`;
      const xml = await readPart(zip, path);
      if (xml) return xml;
    }
  }
  return readPart(zip, 'ppt/theme/theme1.xml');
}

async function parseSlideMasters(
  zip: JSZip,
  presRels: Map<string, string>,
  theme: PptxTheme,
  rawParts: Map<string, string>,
): Promise<PptxSlideMaster[]> {
  const masters: PptxSlideMaster[] = [];

  for (const [, target] of presRels) {
    if (!target.includes('slideMaster')) continue;
    const path = target.startsWith('/') ? target.slice(1) : `ppt/${target}`;
    const xml = await readPart(zip, path);
    if (!xml) continue;
    rawParts.set(path, xml);

    const dom = new DOMParser().parseFromString(xml, 'application/xml');
    const colorMap = mergeColorMap(
      theme.colorMap,
      parseColorMapEl(getEl(dom.documentElement, P_NS, 'clrMap') || getEl(dom.documentElement, A_NS, 'clrMap')),
    );
    const masterTheme = withColorMap(theme, colorMap);
    const cSld = getEl(dom.documentElement, P_NS, 'cSld');
    const shapes = cSld ? parseShapeTree(getEl(cSld, P_NS, 'spTree') || cSld, masterTheme) : [];
    const bgEl = cSld ? getEl(cSld, P_NS, 'bg') : null;
    const background = parseBackground(bgEl, masterTheme);

    masters.push({ partPath: path, background, shapes, colorMap });
  }

  return masters;
}

async function parseSlideLayouts(
  zip: JSZip,
  presRels: Map<string, string>,
  masters: PptxSlideMaster[],
  theme: PptxTheme,
  rawParts: Map<string, string>,
): Promise<PptxSlideLayout[]> {
  const layouts: PptxSlideLayout[] = [];
  const layoutPaths = new Set<string>();

  zip.forEach((relativePath: string) => {
    if (relativePath.includes('slideLayouts/') && relativePath.endsWith('.xml') && !relativePath.includes('_rels')) {
      layoutPaths.add(relativePath);
    }
  });

  for (const path of layoutPaths) {
    const xml = await readPart(zip, path);
    if (!xml) continue;
    rawParts.set(path, xml);

    const dir = path.substring(0, path.lastIndexOf('/'));
    const fileName = path.substring(path.lastIndexOf('/') + 1);
    const layoutRelsPath = `${dir}/_rels/${fileName}.rels`;
    const layoutRelsXml = await readPart(zip, layoutRelsPath);
    const layoutRels = layoutRelsXml ? parseRels(layoutRelsXml) : new Map<string, string>();

    const dom = new DOMParser().parseFromString(xml, 'application/xml');
    let masterRef: string | undefined;
    for (const [, target] of layoutRels) {
      if (target.includes('slideMaster')) {
        masterRef = resolvePath(dir, target);
        break;
      }
    }

    const baseColorMap = masterRef
      ? (masters.find((master) => master.partPath === masterRef)?.colorMap || theme.colorMap)
      : theme.colorMap;
    const colorOverride = parseColorMapOverride(dom.documentElement);
    const colorMap = colorOverride === 'master'
      ? baseColorMap
      : mergeColorMap(baseColorMap, colorOverride);
    const layoutTheme = withColorMap(theme, colorMap);
    const cSld = getEl(dom.documentElement, P_NS, 'cSld');
    const shapes = cSld ? parseShapeTree(getEl(cSld, P_NS, 'spTree') || cSld, layoutTheme) : [];
    const bgEl = cSld ? getEl(cSld, P_NS, 'bg') : null;
    const background = parseBackground(bgEl, layoutTheme);

    layouts.push({ partPath: path, background, shapes, masterRef, colorMap });
  }

  return layouts;
}

/* ------------------------------------------------------------------ */
/* 미디어 해석 헬퍼 (슬라이드별 rId → data URL)                            */
/* ------------------------------------------------------------------ */

export function resolveMediaUrl(
  slidePartPath: string,
  rId: string,
  media: Map<string, string>,
): string | undefined {
  const key = `${slidePartPath}::${rId}`;
  return media.get(key);
}
